<template>
  <section ref="root" class="filters-bar" :aria-label="`${entityLabel} payment filters`" @keydown.esc="close">
    <div class="filters-bar-start"><AppointmentIcon name="filter" /></div>
    <FilterMotion :expanded="editing === 'supplier' && !filters.supplierId">
      <div v-if="filters.supplierId" class="filter-chip">
        <span class="filter-chip-text">{{ selectedName }}</span>
        <button type="button" class="filter-chip-remove" :aria-label="`Clear ${entityLabel.toLowerCase()} filter`" @click="setFilter('supplierId', '')">
          <AppointmentIcon name="close" :size="14" />
        </button>
      </div>
      <div v-else-if="editing === 'supplier'" class="filter-inline-editor">
        <CatalogSearchInput ref="catalog" use-fixed :model-value="filters.supplierId" :options="options"
          :placeholder="`Search ${entityLabel.toLowerCase()}...`" class="filter-editor-search"
          @update:model-value="setFilter('supplierId', $event)" />
      </div>
      <button v-else type="button" class="filter-label-btn" @click="openSupplier">{{ entityLabel }}</button>
    </FilterMotion>
    <div v-if="filters.status" class="filter-chip">
      <span class="filter-chip-text">{{ statusLabel }}</span>
      <button type="button" class="filter-chip-remove" aria-label="Clear status filter" @click="setFilter('status', '')">
        <AppointmentIcon name="close" :size="14" />
      </button>
    </div>
    <div v-else class="status-dropdown-wrapper">
      <button type="button" class="filter-label-btn" aria-haspopup="true" :aria-expanded="editing === 'status'"
        @click="editing = editing === 'status' ? null : 'status'">Status</button>
      <div v-if="editing === 'status'" class="status-dropdown">
        <button v-for="status in statuses" :key="status.value" type="button" class="status-dropdown-option"
          @click="setFilter('status', status.value)">{{ status.label }}</button>
      </div>
    </div>
    <div class="filter-date-wrapper" :class="{ 'has-value': filters.dateFrom || filters.dateTo }" @click="close">
      <DateRangePicker use-fixed :date-from="filters.dateFrom" :date-to="filters.dateTo" placeholder="Date range" :show-trigger-icon="false"
        @update="setDates">
        <template #previous-icon><AppointmentIcon name="chevron-left" /></template>
        <template #next-icon><AppointmentIcon name="chevron-right" /></template>
      </DateRangePicker>
      <button v-if="filters.dateFrom || filters.dateTo" type="button" class="filter-chip-remove" aria-label="Clear date range filter"
        @click="setDates({ dateFrom: '', dateTo: '' })"><AppointmentIcon name="close" :size="14" /></button>
    </div>
  </section>
</template>
<script setup>
import { computed, nextTick, onDeactivated, onMounted, onBeforeUnmount, ref } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import FilterMotion from '@/components/journal/FilterMotion.vue';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
const props = defineProps({
  filters: { type: Object, required: true },
  options: { type: Array, default: () => [] },
  entityLabel: { type: String, default: 'Supplier' },
  statuses: { type: Array, default: () => [{ value: 'unpaid', label: 'Unpaid' }, { value: 'paid', label: 'Paid' }] },
});
const emit = defineEmits(['change']);
const root = ref(null), catalog = ref(null), editing = ref(null);
const selectedName = computed(() => {
  const option = props.options.find(item => String(item.id) === String(props.filters.supplierId));
  return option?.label || option?.name || props.filters.supplierId;
});
const statusLabel = computed(() => props.statuses.find(item => item.value === props.filters.status)?.label || props.filters.status);
const close = () => { editing.value = null; };
const openSupplier = async () => {
  editing.value = 'supplier';
  await nextTick();
  catalog.value?.focusInput();
};
const setFilter = (key, value) => { emit('change', { [key]: value || '' }); close(); };
const setDates = ({ dateFrom, dateTo }) => { emit('change', { dateFrom: dateFrom || '', dateTo: dateTo || '' }); close(); };
// Keep the current input mounted until the next control receives its completed click.
const outsideClick = event => { if (!root.value?.contains(event.target)) close(); };
onMounted(() => document.addEventListener('click', outsideClick, true));
onBeforeUnmount(() => document.removeEventListener('click', outsideClick, true));
onDeactivated(close);
</script>
<style scoped>
/* Shared journal filter styling. */
.filters-bar {
	display: flex;
	align-items: center;
	gap: 8px;
	flex: 1 1 auto;
	min-width: 0;
	max-width: 100%;
	min-height: 36px;
	padding: 2px 12px;
	box-sizing: border-box;
	flex-wrap: wrap;
	position: relative;
	background: var(--noc-background, #fff);
	z-index: 40;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	box-shadow: none;
}

.filters-bar.is-disabled {
	opacity: 0.7;
	pointer-events: none;
}

.filters-bar-start {
	display: flex;
	align-items: center;
	color: var(--text-color-secondary);
}

.filter-chip {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	height: 30px;
	padding: 0 4px 0 10px;
	border: 1px solid var(--border-color);
	border-radius: 6px;
	background: var(--noc-background);
	font: var(--ap-type-control);
	color: var(--text-color);
	white-space: nowrap;
	box-shadow: none;
}

.filter-chip-text {
	max-width: 240px;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: inherit;
}

.filter-chip-remove {
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	cursor: pointer;
	color: var(--text-color-secondary, #9ca3af);
	padding: 4px;
	border-radius: 4px;
	font-size: 0.8rem;
	transition: color 0.15s, background-color 0.15s;
}

.filter-chip-remove:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-label-btn {
	display: inline-flex;
	align-items: center;
	height: 30px;
	padding: 0 10px;
	border: none;
	background: none;
	color: var(--text-color-secondary);
	font: var(--ap-type-control);
	cursor: pointer;
	border-radius: 6px;
	white-space: nowrap;
}

.filter-label-btn:hover:not(:disabled) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-inline-editor {
	position: relative;
	width: 100%;
	min-width: 0;
}

.filter-editor-search {
	width: 100%;
}

.filter-editor-search :deep(.field-input) {
	border: 1px solid var(--border-color);
	border-radius: 6px;
	padding: 6px 10px;
	font: var(--ap-type-body);
	height: 30px;
}

.filter-editor-search :deep(.field-input):focus {
	border-color: var(--noc-blue, #0065ff);
}

.filter-editor-input {
	height: 30px;
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 6px;
	padding: 6px 10px;
	font: var(--ap-type-body);
	outline: none;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	width: 100%;
	box-sizing: border-box;
}

.filter-editor-input:focus {
	border-color: var(--noc-blue);
}

.status-dropdown-wrapper {
	position: relative;
}

.status-dropdown {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	min-width: 120px;
	background: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 8px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	padding: 4px;
	z-index: 100;
	display: flex;
	flex-direction: column;
}

.status-dropdown-option {
	display: block;
	width: 100%;
	padding: 8px 12px;
	background: none;
	border: none;
	text-align: left;
	font: var(--ap-type-control);
	color: var(--text-color, #333);
	cursor: pointer;
	border-radius: 6px;
	transition: background-color 0.15s;
}

.status-dropdown-option:hover {
	background: var(--noc-background-secondary, #f5f5f5);
}

.filter-date-wrapper {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	max-width: 100%;
	border: 1px solid transparent;
	border-radius: 6px;
	box-sizing: border-box;
}

.filter-date-wrapper.has-value {
	border-color: var(--border-color);
}

.filter-date-wrapper :deep(.date-range-picker) {
	min-width: 0;
	max-width: 100%;
}

.filter-date-wrapper :deep(.drp-trigger) {
	border: none;
	background: transparent;
	padding: 0 10px;
	height: 28px;
	font: var(--ap-type-control);
	color: var(--text-color-secondary);
	border-radius: 6px;
	box-shadow: none;
	font-variant-numeric: tabular-nums;
}

.filter-date-wrapper :deep(.drp-trigger:hover:not(:disabled)) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-date-wrapper :deep(.drp-trigger--active) {
	color: var(--text-color);
}

.filter-date-wrapper :deep(.drp-trigger-text) {
	color: inherit;
}



</style>
